import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/solid';
import ApiRequest from '../../../../../api/ApiRequest';
import { API_AUTH_TYPES, API_ROUTES } from '../../../../../api/apiConstants';
import { classNames } from '../../../../misc/styling';
import WaveformVisualizer from './WaveformVisualizer';

const SAMPLE_RATE = 16000;
const ASSEMBLYAI_WS_URL = 'wss://streaming.assemblyai.com/v3/ws';

/**
 * StreamingSpeechToText - Microphone button that streams audio to AssemblyAI via WebSocket and
 * delivers real-time transcripts to the caller. Uses `forwardRef` so the parent can call
 * `ref.current.stopRecording()` or `ref.current.startRecording()` programmatically.
 *
 * Flow: fetch token → getUserMedia → connect WebSocket → send PCM chunks → receive Turn events.
 *
 * @param {Object} props
 * @param {Function} [props.onTranscript] - Called with the final formatted transcript string when a turn ends.
 * @param {Function} [props.onPartialTranscript] - Called with partial (unformatted) transcript strings.
 * @param {Function} [props.onRecordingStart] - Called when recording begins successfully.
 * @param {Function} [props.onRecordingEnd] - Called when recording is stopped.
 * @param {boolean} [props.disabled=false] - Disables the button when true.
 * @param {string} [props.authType=API_AUTH_TYPES.USER] - Auth type used for the token API call.
 * @param {React.Ref} ref - Imperative handle exposing `{ startRecording, stopRecording, isRecording }`.
 * @returns {JSX.Element}
 */
const StreamingSpeechToText = forwardRef(({ onTranscript, onPartialTranscript, onRecordingStart, onRecordingEnd, disabled = false, authType = API_AUTH_TYPES.USER }, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false); // Use ref for animation loop

  const assemblyaiApi = new ApiRequest(API_ROUTES.ASSEMBLYAI, authType);

  /**
   * Tears down all recording resources: cancels the animation frame, closes the WebSocket,
   * stops all media tracks, and closes the AudioContext.
   * @returns {void}
   */
  const cleanup = useCallback(() => {
    isRecordingRef.current = false;

    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close WebSocket
    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN) {
        // Send termination message
        try {
          socketRef.current.send(JSON.stringify({ type: 'Terminate' }));
        } catch (e) {
          console.error('Error sending termination:', e);
        }
      }
      socketRef.current.close();
      socketRef.current = null;
    }

    // Stop media tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    processorRef.current = null;
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  /**
   * Starts a `requestAnimationFrame` loop that reads audio frequency data from the AnalyserNode
   * and updates `audioLevel` (normalized 0–1) for the waveform visualizer.
   * @returns {void}
   */
  const startAudioLevelLoop = useCallback(() => {
    const updateLevel = () => {
      if (!isRecordingRef.current || !analyserRef.current) {
        return;
      }

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);

      // Calculate average level with emphasis on voice frequencies
      let sum = 0;
      let count = 0;
      // Focus on frequencies 100-3000 Hz (voice range)
      const minBin = Math.floor(100 / (SAMPLE_RATE / analyserRef.current.fftSize));
      const maxBin = Math.floor(3000 / (SAMPLE_RATE / analyserRef.current.fftSize));

      for (let i = minBin; i < Math.min(maxBin, dataArray.length); i++) {
        sum += dataArray[i];
        count++;
      }

      const average = count > 0 ? sum / count : 0;
      setAudioLevel(average / 255); // Normalize to 0-1

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, []);

  /**
   * Fetches a short-lived AssemblyAI streaming token, requests microphone access,
   * sets up the Web Audio pipeline, and connects to the AssemblyAI WebSocket to begin streaming.
   * @returns {Promise<void>}
   */
  const startRecording = async () => {
    setError(null);
    setIsConnecting(true);

    try {
      // Get temporary token from server
      console.log('[StreamingSpeechToText] Fetching token...');
      const response = await assemblyaiApi.get({ endpoint: 'streaming_token' });
      const token = response?.token;
      const speechModel = response?.model;

      if (!token) {
        throw new Error('Failed to get streaming token from server');
      }
      if (!speechModel) {
        throw new Error('Failed to get streaming model from server');
      }

      // Get microphone access
      console.log('[StreamingSpeechToText] Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      console.log('[StreamingSpeechToText] Microphone access granted');
      mediaStreamRef.current = stream;

      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });

      // Wait for audio context to be running
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      console.log('[StreamingSpeechToText] Audio context state:', audioContextRef.current.state);

      // Create analyser for visualization
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      // Create media stream source
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Create script processor for audio data
      processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      source.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      // Build WebSocket URL with parameters
      const wsParams = new URLSearchParams({
        token,
        speech_model: speechModel,
        sample_rate: SAMPLE_RATE.toString(),
        encoding: 'pcm_s16le',
      });
      const wsUrl = `${ASSEMBLYAI_WS_URL}?${wsParams.toString()}`;

      // Connect to AssemblyAI WebSocket
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('[StreamingSpeechToText] WebSocket connected');
        setIsConnecting(false);
        setIsRecording(true);
        isRecordingRef.current = true;
        onRecordingStart?.();

        // Start audio level visualization
        startAudioLevelLoop();

        // Start sending audio data as raw PCM binary frames.
        processorRef.current.onaudioprocess = (event) => {
          if (socket.readyState === WebSocket.OPEN) {
            const inputData = event.inputBuffer.getChannelData(0);
            // Convert float32 to int16
            const int16Data = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              const s = Math.max(-1, Math.min(1, inputData[i]));
              int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            socket.send(int16Data.buffer);
          }
        };
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[StreamingSpeechToText] Message received:', data.type, data.transcript?.substring(0, 50));

          if (data.type === 'Begin') {
            console.log('[StreamingSpeechToText] Session started:', data.id);
          } else if (data.type === 'Turn') {
            const transcript = data.transcript || '';
            if (transcript && data.end_of_turn) {
              console.log('[StreamingSpeechToText] Final transcript:', transcript);
              onTranscript?.(transcript);
            } else if (transcript) {
              onPartialTranscript?.(transcript);
            }
          } else if (data.type === 'Termination') {
            console.log('[StreamingSpeechToText] Session terminated');
          } else if (data.error) {
            console.error('[StreamingSpeechToText] Server error:', data);
            setError(data.error);
          }
        } catch (e) {
          console.error('[StreamingSpeechToText] Error parsing message:', e);
        }
      };

      socket.onerror = (event) => {
        console.error('[StreamingSpeechToText] WebSocket error:', event);
        setError('Connection error occurred');
        cleanup();
        setIsRecording(false);
        setIsConnecting(false);
        onRecordingEnd?.();
      };

      socket.onclose = (event) => {
        console.log('[StreamingSpeechToText] WebSocket closed:', event.code, event.reason);
        if (event.code !== 1000 && event.code !== 1005) {
          // Abnormal closure - show error
          setError(`Connection closed: ${event.reason || `code ${event.code}`}`);
        }
        cleanup();
        setIsRecording(false);
        setIsConnecting(false);
        onRecordingEnd?.();
      };

    } catch (err) {
      console.error('[StreamingSpeechToText] Error starting recording:', err);
      setError(err.message || 'Failed to start recording');
      cleanup();
      setIsRecording(false);
      setIsConnecting(false);
      onRecordingEnd?.();
    }
  };

  /**
   * Stops recording by calling `cleanup` and notifying the parent via `onRecordingEnd`.
   * @returns {void}
   */
  const stopRecording = useCallback(() => {
    cleanup();
    setIsRecording(false);
    setIsConnecting(false);
    onRecordingEnd?.();
  }, [cleanup, onRecordingEnd]);

  // Expose stopRecording and isRecording to parent via ref
  useImperativeHandle(ref, () => ({
    stopRecording,
    isRecording,
    startRecording
  }), [stopRecording, isRecording]);

  /**
   * Toggles recording on/off. Stops if already recording; starts if idle and not connecting.
   * @returns {void}
   */
  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else if (!isConnecting) {
      startRecording();
    }
  };

  return (
    <div className="relative flex items-center">
      {/* Waveform visualization - shows when recording */}
      {/* {isRecording && (
        <div className="mr-2">
          <WaveformVisualizer audioLevel={audioLevel} isActive={isRecording} />
        </div>
      )} */}

      {/* Microphone button */}
      <button
        onClick={handleClick}
        disabled={disabled || isConnecting}
        className={classNames(
          "relative p-1.5 rounded-lg transition-all duration-200 flex-shrink-0",
          isRecording
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : isConnecting
              ? "bg-gray-200 text-gray-400 cursor-wait"
              : disabled
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        )}
        title={isRecording ? "Stop recording" : isConnecting ? "Connecting..." : "Start voice input"}
      >
        {isRecording ? (
          <StopIcon className="h-4 w-4" />
        ) : (
          <MicrophoneIcon className={classNames(
            "h-4 w-4",
            isConnecting && "animate-pulse"
          )} />
        )}

        {/* Recording indicator dot */}
        {isRecording && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-blue-400 rounded-full animate-ping" />
        )}
      </button>

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-0 mb-2 px-2 py-1 text-xs text-red-600 bg-red-50 rounded shadow-sm whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
});

StreamingSpeechToText.displayName = 'StreamingSpeechToText';

export default StreamingSpeechToText;

// Also export a named version for explicit imports
export { StreamingSpeechToText };

